import React, {
  useState,
  memo,
  useCallback,
  useContext,
  createRef,
  useEffect,
} from 'react'
import { arrayOf, oneOfType, shape, node, func, string, bool } from 'prop-types'
import { Dropdown, DropdownList, Button } from '@livechat/design-system'
import { css } from '@emotion/react'

import { useOutsideClick } from '@/hooks'
import { EnhancedListContext } from '@/context/EnhancedList'
import FilterItem from './FilterItem'

const dropdownListCss = css`
  min-width: 200px;
  max-height: 50vh;
`

const filterWrapperCss = css`
  max-width: 100%;
`

const filterButtonCss = css`
  justify-content: flex-start;
  max-width: 100%;

  > div {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .lc-btn__icon svg {
    fill: none;
  }
`

const Filter = ({
  id,
  label,
  icon,
  options,
  multiselect,
  onSelect,
  value,
  displayCustomValue,
  dataTest,
}) => {
  const { filters } = useContext(EnhancedListContext)

  const wrapperRef = createRef(null)
  const filterValue = filters?.[id]

  const handleSelect = (value) => {
    const updatedValues = values.map((item) => {
      if (value === item.itemId) {
        item.isSelected = !item.isSelected
      }

      if (!multiselect && value !== item.itemId && item.isSelected) {
        item.isSelected = false
      }

      return item
    })

    setValues(updatedValues)

    if (onSelect) {
      onSelect(id, value, multiselect)
    }
  }

  const checkIfSelected = (filterValue, fieldValue) => {
    if (Array.isArray(filterValue)) {
      return filterValue.includes(fieldValue)
    } else {
      return filterValue === fieldValue
    }
  }

  const prepareValues = useCallback(
    (values) =>
      values.map(({ LABEL, VALUE }, index) => ({
        key: `${VALUE}-${index}`,
        itemId: VALUE,
        content: LABEL,
        onItemSelect: () => handleSelect(VALUE),
        isSelected: checkIfSelected(value, VALUE),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, value]
  )

  const [values, setValues] = useState(prepareValues(options))
  const [isVisible, setIsVisible] = useState(false)
  const toggleVisibility = () => setIsVisible(!isVisible)

  const getSelectedValueLabel = (value) => {
    let selectedValue = null

    switch (true) {
      case !!displayCustomValue:
        selectedValue = displayCustomValue(value)
        break

      case value && Array.isArray(value):
        const selectedValues = []

        value.forEach((val) => {
          const selected = values.find(({ itemId }) => itemId === val)

          if (selected && selected.content) {
            selectedValues.push(selected.content)
          }
        })

        if (selectedValues.length) {
          selectedValue = selectedValues.join(', ')
        }
        break

      case value && typeof value === 'string':
        const selected = values.find(({ itemId }) => itemId === value)

        if (selected && selected.content) {
          selectedValue = selected.content
        }
        break

      default:
    }

    return `: ${selectedValue || 'all'}`
  }

  useOutsideClick(() => {
    if (isVisible) {
      toggleVisibility()
    }
  }, wrapperRef)

  useEffect(() => {
    setValues(prepareValues(options))
  }, [filterValue, options, prepareValues])

  return (
    <div ref={wrapperRef} css={filterWrapperCss}>
      <Dropdown
        isVisible={isVisible}
        placement="bottom-start"
        modifiers={{
          flip: {
            enabled: false,
          },
        }}
        triggerRenderer={({ ref }) => (
          <Button
            css={filterButtonCss}
            kind="secondary"
            ref={ref}
            icon={icon}
            onClick={toggleVisibility}
            data-test={dataTest}
          >
            <span>{label}</span>
            <span>{getSelectedValueLabel(value)}</span>
          </Button>
        )}
      >
        <DropdownList
          getItemBody={(props) => (
            <FilterItem
              // eslint-disable-next-line react/prop-types
              key={props.itemId}
              {...props}
              onSelectItem={handleSelect}
            />
          )}
          css={dropdownListCss}
          items={values}
        />
      </Dropdown>
    </div>
  )
}

Filter.propTypes = {
  options: arrayOf(
    shape({
      LABEL: string,
      VALUE: string,
    })
  ),
  multiselect: bool,
  onSelect: func,
  id: string,
  label: string,
  icon: node,
  value: oneOfType([string, arrayOf(string)]),
  displayCustomValue: func,
  dataTest: string,
}

export default memo(Filter)
